import Link from 'next/link'
import { FunctionComponent, ReactText, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import { LeftChevron, RightChevron } from 'components/SVG/Chevron'
import Spacer from 'components/Spacer'

import { interleaveArrayWithElement } from 'utils/arrayHelpers'
import getPagination from 'utils/getPagination'

const Container = styled.div`
  display: flex;
  align-items: center;
`

interface ISelectedProps {
  selected?: boolean
  hidePointer?: boolean
}
const Number = styled.div<ISelectedProps>`
  height: 24px;
  min-width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => props.theme.colors[props.selected ? 'blue4' : 'transparent']};
  color: ${props => props.theme.colors[props.selected ? 'white' : 'transparentBlackAlpha50']};
  border-radius: 3px;

  ${({ selected }) =>
    selected
    && `
    min-width: 24px;
  `}

  padding-top: 2px; /* font correction */
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: ${props => (props.hidePointer ? 'inherit' : 'pointer')};
`

const ChevronContainer = styled.div`
  height: 20px;
  width: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

interface IRenderPageNums {
  (
    pages: ReactText[],
    pathname: string | null | undefined,
    queryParams:
      | {
          [key: string]: string
        }
      | undefined,
    currentPage: number,
  ): JSX.Element[]
}

const renderPageNums: IRenderPageNums = (pages, pathname, queryParams, currentPage) => {
  const pageNums = pages.map((pageNum: number | string, index: number) => {
    const key = `ellipsis-${index}`

    if (pageNum === '...') {
      return (
        <Number
          hidePointer
          key={key}
        >
          ...
        </Number>
      )
    }

    return (
      <Link
        href={{ pathname, query: { ...queryParams, page: pageNum } }}
        scroll={false}
        key={pageNum}
      >
        <Number selected={currentPage === pageNum}>{pageNum}</Number>
      </Link>
    )
  })

  return interleaveArrayWithElement(pageNums, <Spacer size={14} />) as JSX.Element[]
}

interface IProps {
  currentPage: number
  pageSize?: number
  // maxPagesShown?: number, // deprecated, hardcoded to max of 9 at once for stepper logic
  totalItems?: number
  pathname: string
  queryParams?: { [key: string]: string }
}

const PageStepper: FunctionComponent<IProps> = ({
  currentPage,
  pageSize = 24,
  totalItems = 0,
  pathname,
  queryParams,
}) => {
  const theme = useContext(ThemeContext)

  // calculate total pages
  const totalPages = Math.ceil(totalItems / pageSize)
  const pages = getPagination(currentPage, totalPages)

  const onFirstPage = currentPage === 1 || !currentPage
  const onLastPage = currentPage === totalPages

  const prevPage = onFirstPage ? 1 : currentPage - 1
  const nextPage = onLastPage ? currentPage : currentPage + 1

  if (totalPages <= 1) return null

  return (
    <Container>
      <Link
        href={{ pathname, query: { ...queryParams, page: prevPage } }}
        scroll={false}
      >
        <ChevronContainer>
          <LeftChevron fill={theme.colors[onFirstPage ? 'grey4' : 'black']} />
        </ChevronContainer>
      </Link>
      <Spacer size={10} />
      {renderPageNums(pages, pathname, queryParams, currentPage)}
      <Spacer size={10} />
      <Link
        href={{ pathname, query: { ...queryParams, page: nextPage } }}
        scroll={false}
        replace
      >
        <ChevronContainer>
          <RightChevron fill={theme.colors[onLastPage ? 'grey4' : 'black']} />
        </ChevronContainer>
      </Link>
    </Container>
  )
}

export default PageStepper
